import { useRecoilValue } from 'recoil';
import { hasAuthHint, isLoggedInAtom, userAtom } from '@/state/loginState';

/**
 * 속닥속닥 글 공간 판정(2026-10-10, docs/sokdak-doctor-space-plan.md · 2026-10-11 면허 인증 기준).
 *
 * 속닥속닥은 두 공간으로 나뉜다 — 치과위생사(스탭) 공간 / 치과의사(원장) 공간. 서로의 글은 읽기·쓰기 모두 안 된다.
 * - 치과회원(병원, ROLE_CLINIC)·**면허 인증된 치과의사** = 치과의사 공간
 * - 직군만 치과의사이고 면허 인증 전 = 어느 공간도 아님(LICENSE_REQUIRED — 인증 안내)
 * - 나머지 회원·비로그인 = 치과위생사 공간
 * 공간은 **서버가 면허 인증 기록으로 정해 회원 정보(postSpace)에 실어 준다**. 직군(userTypeCode)은 회원이 확인 없이 바꾸는 값이라
 * 화면이 다시 판정하지 않는다 — postSpace 가 없는 옛 서버 응답일 때만 예전 판정으로 대신한다.
 * 실제로 어떤 글을 줄지는 서버가 정한다. 화면의 판정은 공간 라벨·안내 문구를 고르는 데만 쓴다.
 */
type PostAccessUser = Pick<User, 'role' | 'userTypeCode' | 'postSpace'> | null | undefined;

export type PostSpace = 'STAFF' | 'DOCTOR';

/** 회원의 속닥속닥 이용 상태 — 공간 둘 + 면허 인증 후 이용 */
export type PostSpaceAccess = PostSpace | 'LICENSE_REQUIRED';

/** 서버가 면허 인증 전 치과의사에게 주는 403 구분 코드(result.errorCode) */
export const POST_SPACE_LICENSE_REQUIRED_CODE: string = 'POST_SPACE_LICENSE_REQUIRED';

/** 응답 오류가 "면허 인증 후 이용" 인지 */
export const isPostSpaceLicenseRequiredError = (error: any): boolean =>
    error?.response?.status === 403 && error?.response?.data?.result?.errorCode === POST_SPACE_LICENSE_REQUIRED_CODE;

/** 공간 라벨 — 속닥속닥 목록 필터 줄·글쓰기 안내에 쓴다 */
export const POST_SPACE_LABEL: Record<PostSpace, string> = {
    STAFF: '치과위생사',
    DOCTOR: '치과의사',
};

/** 치과회원(병원 계정) — 치과 계정도 userTypeCode 가 DENTIST 라서 역할을 먼저 본다 */
export const isClinicMemberUser = (user: PostAccessUser): boolean =>
    Array.isArray(user?.role) && user.role.includes('ROLE_CLINIC');

/** 치과의사(원장) 개인 회원 — 치과회원이 아닌 DENTIST */
export const isDentistMemberUser = (user: PostAccessUser): boolean =>
    !isClinicMemberUser(user) && user?.userTypeCode === 'DENTIST';

/** 서버가 준 공간 값 — 모르는 값·옛 서버(없음)면 null */
const serverSpaceOf = (user: PostAccessUser): PostSpaceAccess | null => {
    const value = user?.postSpace;
    return value === 'STAFF' || value === 'DOCTOR' || value === 'LICENSE_REQUIRED' ? value : null;
};

/** 회원의 속닥속닥 이용 상태(서버 PostAudience.of 와 같은 기준 — 서버 값 우선) */
export const postSpaceAccessOf = (user: PostAccessUser): PostSpaceAccess => {
    const fromServer: PostSpaceAccess | null = serverSpaceOf(user);
    if (fromServer) return fromServer;
    // 옛 서버(postSpace 없음) — 예전 판정. 서버가 실제 글을 거르므로 라벨만 틀릴 수 있다
    return isClinicMemberUser(user) || isDentistMemberUser(user) ? 'DOCTOR' : 'STAFF';
};

/** 치과의사 공간 회원인지(치과회원·면허 인증된 치과의사). 비로그인·프로필 로드 전(null)은 false */
export const isDoctorSpaceUser = (user: PostAccessUser): boolean => postSpaceAccessOf(user) === 'DOCTOR';

/**
 * 라벨·예시 질문을 고를 공간 — 면허 인증 전 치과의사는 치과의사 쪽 문구를 쓴다(글은 서버가 막는다).
 * 실제 이용 가능 여부는 [postSpaceAccessOf] 로 본다.
 */
export const postSpaceOf = (user: PostAccessUser): PostSpace => (postSpaceAccessOf(user) === 'STAFF' ? 'STAFF' : 'DOCTOR');

/** 속닥속닥 화면 주소인지 — `/posts`, `/posts/123`, `/posts?search=…` */
export const isPostPath = (path: string | null | undefined): boolean =>
    !!path && /^\/posts(?:[/?#]|$)/.test(path);

/**
 * 지금 회원의 속닥속닥 이용 상태. 로그인했는데 프로필을 아직 못 읽었으면 null — 라벨이 치과위생사 → 치과의사로 바뀌어 보이지 않게
 * 그동안은 라벨을 그리지 않는다.
 */
export const usePostSpace = (): PostSpaceAccess | null => {
    const user = useRecoilValue(userAtom);
    const isLoggedIn: boolean = useRecoilValue(isLoggedInAtom);
    if (user) return postSpaceAccessOf(user);
    if (isLoggedIn || hasAuthHint()) return null;
    return 'STAFF';
};
